// Paste-tolerant parsing for the guided Telegram setup inputs. Customers
// routinely paste BotFather's whole "Done! ... Use this token: <token>"
// message instead of just the token (and the whole bot reply instead of just
// the pairing code), so we extract the value rather than rejecting it.

// Telegram bot token = `<bot_id>:<auth>`: a numeric bot id (the original 9
// digits has grown as Telegram ids increased, so 5-16) + a colon + a 35-char
// auth string from [A-Za-z0-9_-]. Format confirmed across token-scanning
// projects, e.g. https://github.com/gitleaks/gitleaks/pull/1404.
const BOT_TOKEN_STRICT = /^[0-9]{5,16}:[A-Za-z0-9_-]{35}$/;
const BOT_TOKEN_ANYWHERE = /[0-9]{5,16}:[A-Za-z0-9_-]{35}/;

// Returns the bot token from `input` (the value itself, or the one embedded in
// a pasted BotFather message), or null if none is present.
export const extractBotToken = (input: string): string | null => {
  const trimmed = input.trim();
  if (BOT_TOKEN_STRICT.test(trimmed)) return trimmed;
  const match = trimmed.match(BOT_TOKEN_ANYWHERE);
  return match ? match[0] : null;
};

// OpenClaw device-pairing codes are short, uppercase alphanumeric (e.g.
// "4FHTUTB8"). We range-check 6-12 chars rather than pinning an exact length,
// so minor format drift doesn't break us, while staying short enough not to
// collide with the (longer, digits-only) Telegram user id printed just above
// the code in the bot's reply.
const PAIRING_BARE = /^[A-Za-z0-9]{6,12}$/;
// In a pasted whole reply, pull the code after the "Pairing code" label (or the
// "approve telegram <code>" command line) — never the "user id" line.
const PAIRING_LABELLED = /pairing code[^A-Za-z0-9]*([A-Za-z0-9]{6,12})/i;
const PAIRING_APPROVE = /approve telegram\s+([A-Za-z0-9]{6,12})/i;

// Returns the normalized (upper-cased) pairing code from `input`, or null.
export const extractPairingCode = (input: string): string | null => {
  const trimmed = input.trim();
  if (PAIRING_BARE.test(trimmed)) return trimmed.toUpperCase();
  const labelled = trimmed.match(PAIRING_LABELLED);
  if (labelled && labelled[1]) return labelled[1].toUpperCase();
  const approve = trimmed.match(PAIRING_APPROVE);
  if (approve && approve[1]) return approve[1].toUpperCase();
  return null;
};
